// Shared Webhook schema + helpers (mirrors backend Step 2 & Step 4)
// No external schema lib used to avoid new deps. Keep lightweight and robust.

const MAX_URL_LEN = 2048;
const MAX_TEMPLATE_BYTES = 8 * 1024; // ~8KB
const MAX_DEPTH = 5;

export function isHttpsUrl(url) {
  try {
    const u = new URL(String(url || ''));
    return u.protocol === 'https:' && String(url).length <= MAX_URL_LEN;
  } catch (_) {
    return false;
  }
}

function _byteLen(v) {
  try {
    if (typeof v === 'string') return new TextEncoder().encode(v).length;
    return new TextEncoder().encode(JSON.stringify(v)).length;
  } catch (_) {
    return Infinity;
  }
}

function _depthOk(obj, maxDepth = MAX_DEPTH) {
  const seen = new WeakSet();
  function walk(o, d) {
    if (o === null || typeof o !== 'object') return true;
    if (seen.has(o)) return true; // avoid cycles
    seen.add(o);
    if (d > maxDepth) return false;
    for (const k of Object.keys(o)) {
      if (!walk(o[k], d + 1)) return false;
    }
    return true;
  }
  return walk(obj, 1);
}

function _normalizeHeaders(input) {
  const out = {};
  const errors = [];
  if (!input || typeof input !== 'object') return { headers: out, errors };
  const allow = new Set(['Content-Type', 'Authorization']);
  for (const [k, v] of Object.entries(input)) {
    if (!allow.has(k)) continue;
    if (k === 'Authorization') {
      const s = String(v || '').trim();
      if (!s.startsWith('secret:')) {
        errors.push('Authorization must be a secret ref like "secret:MY_REF"');
        continue;
      }
      out[k] = s;
    } else if (k === 'Content-Type') {
      out[k] = String(v || '').trim() || 'application/json';
    }
  }
  return { headers: out, errors };
}

export const webhookSchema = {
  validate(values) {
    const errors = {};
    const normalized = {};

    // label
    const label = String((values?.label ?? '').toString().trim());
    if (!label || label.length < 1 || label.length > 100) {
      errors.label = 'Label must be 1–100 characters';
    } else {
      normalized.label = label;
    }

    // url
    const url = values?.url ?? values?.webhookUrl;
    if (!isHttpsUrl(url)) {
      errors.url = 'Webhook URL must be HTTPS and <= 2048 chars';
    } else {
      normalized.url = String(url).trim();
    }

    // enabled
    normalized.enabled = values?.enabled === undefined ? true : !!values.enabled;

    // headers (allowlist)
    const { headers, errors: headerErrs } = _normalizeHeaders(values?.headers || {});
    if (headerErrs.length) errors.headers = headerErrs.join('; ');
    normalized.headers = headers;

    // messageTemplate (string or object)
    const mt = values?.messageTemplate;
    if (typeof mt === 'string') {
      const s = mt;
      if (_byteLen(s) > MAX_TEMPLATE_BYTES) {
        errors.messageTemplate = 'Template too large (>8KB)';
      } else {
        normalized.messageTemplate = s;
      }
    } else if (mt && typeof mt === 'object') {
      if (!_depthOk(mt, MAX_DEPTH)) {
        errors.messageTemplate = 'Template too deep (>5 levels)';
      } else if (_byteLen(mt) > MAX_TEMPLATE_BYTES) {
        errors.messageTemplate = 'Template too large (>8KB)';
      } else {
        normalized.messageTemplate = mt;
      }
    } else if (mt === undefined || mt === null) {
      normalized.messageTemplate = '';
    } else {
      errors.messageTemplate = 'Template must be a string or JSON object';
    }

    return {
      success: Object.keys(errors).length === 0,
      errors,
      value: normalized,
    };
  },
};

export function normalizeWebhookFormValues(formValues) {
  const raw = { ...formValues };
  raw.label = String(raw.label || '').trim();
  raw.url = String(raw.url || raw.webhookUrl || '').trim();
  if (raw.webhookUrl) delete raw.webhookUrl;
  if (raw.enabled === undefined) raw.enabled = true;
  if (raw.headers && typeof raw.headers === 'object') {
    const norm = _normalizeHeaders(raw.headers);
    raw.headers = norm.headers;
  } else {
    raw.headers = {};
  }
  // messageTemplate pass-through; schema will bound size/depth
  return raw;
}

export function buildWebhookTestPayload(template, override) {
  const src = override !== undefined ? override : template;
  if (typeof src === 'string') return { message: src };
  if (src && typeof src === 'object') return src;
  return { message: '' };
}
